import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';
import { useEditorStore } from '../stores/useEditorStore';
import { clamp, round } from '../utils/math';

import { getElementBounds } from './bound';
import { getConfig } from './config';

export type Zoom = { value: number };

export const getNormalizedZoom = (zoom: number): number => {
  const config = getConfig();
  return clamp(round(zoom, 6), config.zoom.minZoom, config.zoom.maxZoom);
};

export const getStateForZoom = (payload: {
  nextZoom: number;
  viewportX: number;
  viewportY: number;
}) => {
  const state = useEditorRuntimeStore.getState();

  const { nextZoom, viewportX, viewportY } = payload;

  const appLayerX = viewportX - state.offsetLeft;
  const appLayerY = viewportY - state.offsetTop;

  const currentZoom = state.zoom.value;

  const baseScrollX = state.scrollX + (appLayerX - appLayerX / currentZoom);
  const baseScrollY = state.scrollY + (appLayerY - appLayerY / currentZoom);

  const zoomOffsetScrollX = -(appLayerX - appLayerX / nextZoom);
  const zoomOffsetScrollY = -(appLayerY - appLayerY / nextZoom);

  return {
    scrollX: baseScrollX + zoomOffsetScrollX,
    scrollY: baseScrollY + zoomOffsetScrollY,
    zoom: {
      value: nextZoom,
    },
  };
};

export const constrainPanToElement = (
  scrollX: number,
  scrollY: number,
  elementId: string | undefined,
  viewportWidth: number,
  viewportHeight: number,
  zoom: number,
): { scrollX: number; scrollY: number } => {
  if (!elementId) return { scrollX, scrollY };

  const elementState = useEditorStore.getState();
  const element = elementState.elements.get(elementId);

  if (!element) return { scrollX, scrollY };

  const [minX, minY, maxX, maxY] = getElementBounds(element);

  const centerX = viewportWidth / 2 / zoom - scrollX;
  const centerY = viewportHeight / 2 / zoom - scrollY;

  const clampedCenterX = Math.max(minX, Math.min(maxX, centerX));
  const clampedCenterY = Math.max(minY, Math.min(maxY, centerY));

  const constrainedScrollX = viewportWidth / 2 / zoom - clampedCenterX;
  const constrainedScrollY = viewportHeight / 2 / zoom - clampedCenterY;

  return {
    scrollX: constrainedScrollX,
    scrollY: constrainedScrollY,
  };
};

export const calculateZoomToFitStateFromBounds = (bounds: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  centerX: number;
  centerY: number;
  paddingFactor?: number;
}) => {
  const state = useEditorRuntimeStore.getState();
  const { x1, y1, x2, y2, centerX, centerY, paddingFactor = 0.8 } = bounds;

  const width = x2 - x1;
  const height = y2 - y1;

  if (width <= 0 || height <= 0) return null;

  const targetZoom = Math.min(
    (window.innerWidth * paddingFactor) / width,
    (window.innerHeight * paddingFactor) / height,
  );

  const viewportCenterX = window.innerWidth / 2 + state.offsetLeft;
  const viewportCenterY = window.innerHeight / 2 + state.offsetTop;

  const normalizedZoom = getNormalizedZoom(targetZoom);

  return {
    ...getStateForZoom({
      nextZoom: normalizedZoom,
      viewportX: viewportCenterX,
      viewportY: viewportCenterY,
    }),
    scrollX: (viewportCenterX - state.offsetLeft) / normalizedZoom - centerX,
    scrollY: (viewportCenterY - state.offsetTop) / normalizedZoom - centerY,
  };
};

export const getNextZoomSnap = (
  currentZoom: number,
  direction: 'in' | 'out',
): number => {
  const {
    zoom: { snapPoints },
  } = getConfig();

  if (direction === 'in') {
    for (const snap of snapPoints) {
      if (snap > currentZoom) return snap;
    }

    return snapPoints.at(-1) ?? 1;
  } else {
    for (let i = snapPoints.length - 1; i >= 0; i--) {
      if (snapPoints[i] < currentZoom) return snapPoints[i];
    }
    return snapPoints.at(0) ?? 1;
  }
};
